import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface ScrumUser {
  id: string;
  name: string;
  avatarUrl: string | null;
  // CHG-133 Step 6: 표시 일관성
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface DailyScrumEntry {
  entryId: string;
  dailyScrumId: string;
  userId: string;
  yesterday: unknown[];
  today: unknown[];
  blockers: { content: string; status: string }[];
  user: ScrumUser;
}

export interface DailyScrumSharedItem {
  itemId: string;
  dailyScrumId: string;
  type: 'NOTICE' | 'ACTION_ITEM';
  content: string;
  status: string;
}

export interface DailyScrumDetail {
  dailyScrumId: string;
  projectId: string;
  sprintId: string;
  date: string;
  createdById: string;
  createdAt: string;
  entries: DailyScrumEntry[];
  sharedItems: DailyScrumSharedItem[];
  createdBy: { id: string; name: string };
}

export interface DailyScrumListItem {
  dailyScrumId: string;
  projectId: string;
  sprintId: string;
  date: string;
  createdAt: string;
  _count: { entries: number };
  createdBy: { id: string; name: string };
}

export interface PreviousScrumResult {
  hasPrevious: boolean;
  isConsecutive: boolean;
  scrum: DailyScrumDetail | null;
}

// --- API ---

const base = (projectId: string, sprintId: string) =>
  `/projects/${projectId}/sprints/${sprintId}/scrums`;

export async function fetchDailyScrums(projectId: string, sprintId: string) {
  const res = await api.get<ApiResponse<DailyScrumListItem[]>>(
    base(projectId, sprintId),
  );
  return res.data.data!;
}

export async function fetchDailyScrum(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
) {
  const res = await api.get<ApiResponse<DailyScrumDetail>>(
    `${base(projectId, sprintId)}/${dailyScrumId}`,
  );
  return res.data.data!;
}

export async function createDailyScrum(projectId: string, sprintId: string, date: string) {
  const res = await api.post<ApiResponse<DailyScrumDetail>>(
    base(projectId, sprintId),
    { date },
  );
  return res.data.data!;
}

export async function fetchPreviousScrum(
  projectId: string,
  sprintId: string,
  date: string,
) {
  const res = await api.get<ApiResponse<PreviousScrumResult>>(
    `${base(projectId, sprintId)}/previous?date=${date}`,
  );
  return res.data.data!;
}

// --- Entry APIs ---

export async function updateEntry(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  entryId: string,
  data: { yesterday?: unknown[]; today?: unknown[]; blockers?: unknown[] },
) {
  const res = await api.patch<ApiResponse<DailyScrumEntry>>(
    `${base(projectId, sprintId)}/${dailyScrumId}/entries/${entryId}`,
    data,
  );
  return res.data.data!;
}

export async function addEntry(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  userId: string,
) {
  const res = await api.post<ApiResponse<DailyScrumEntry>>(
    `${base(projectId, sprintId)}/${dailyScrumId}/entries`,
    { userId },
  );
  return res.data.data!;
}

export async function removeEntry(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  entryId: string,
) {
  await api.delete(
    `${base(projectId, sprintId)}/${dailyScrumId}/entries/${entryId}`,
  );
}

export async function removeEntryFromFutureScrums(
  projectId: string,
  sprintId: string,
  userId: string,
  fromDate: string,
) {
  const res = await api.delete<ApiResponse<{ deletedCount: number }>>(
    `${base(projectId, sprintId)}/entries/member/${userId}?fromDate=${fromDate}`,
  );
  return res.data.data!;
}

// --- SharedItem APIs ---

export async function addSharedItem(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  data: { type: string; content: string },
) {
  const res = await api.post<ApiResponse<DailyScrumSharedItem>>(
    `${base(projectId, sprintId)}/${dailyScrumId}/shared-items`,
    data,
  );
  return res.data.data!;
}

export async function updateSharedItem(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  itemId: string,
  data: { content?: string; status?: string },
) {
  const res = await api.patch<ApiResponse<DailyScrumSharedItem>>(
    `${base(projectId, sprintId)}/${dailyScrumId}/shared-items/${itemId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteSharedItem(
  projectId: string,
  sprintId: string,
  dailyScrumId: string,
  itemId: string,
) {
  await api.delete(
    `${base(projectId, sprintId)}/${dailyScrumId}/shared-items/${itemId}`,
  );
}
